import React, { useState, useEffect, useMemo } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { frontendDb as db } from '../lib/frontend-db';
import type { CustomSection } from '../lib/frontend-db';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [customSections, setCustomSections] = useState<CustomSection[]>([]);
  const [headerLogoUrl, setHeaderLogoUrl] = useState('');
  const [contactFormEnabled, setContactFormEnabled] = useState(false);
  const [contactFormTitle, setContactFormTitle] = useState('Kontaktformular');
  const [aboutTitle, setAboutTitle] = useState('Das sind wir!');
  const location = useLocation();

  useEffect(() => {
    loadData();
    
    // Listen for settings changes to update header dynamically
    const handleSettingsChange = () => {
      console.log('🔄 Header: Settings updated event received');
      loadData();
    };

    window.addEventListener('settingsUpdated', handleSettingsChange);
    
    // Add a global function to manually refresh header (for debugging)
    (window as any).refreshHeader = () => {
      console.log('🔄 Manual header refresh triggered');
      loadData();
    };
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsChange);
    };
  }, []);

  const loadData = async () => {
    try {
      console.log('🔄 Header: Loading data...');
      const sections = await db.getCustomSections();
      setCustomSections(sections);
      
      // Load settings for header logo
      const settings = await db.getSettings() as any;
      if (settings) {
        console.log('📋 Header: Settings loaded:', settings);
        setHeaderLogoUrl(settings.header_logo_url || '');
      }
      
      // Load about data for title
      const about = await db.getAbout() as any;
      if (about) {
        const newAboutTitle = about.section_title || 'Das sind wir!';
        console.log('📝 Header: About title loaded from about table:', newAboutTitle);
        setAboutTitle(newAboutTitle);
      }
      
      const contactFormSettings = await db.getContactFormSettings() as any;
      if (contactFormSettings) {
        setContactFormEnabled(contactFormSettings.enabled || false);
        setContactFormTitle(contactFormSettings.section_title || 'Kontaktformular');
      }
      console.log('✅ Header: Data loading completed');
    } catch (error) {
      console.error('❌ Header: Error loading data:', error);
    }
  };

  const sections = useMemo(() => {
    console.log('🔄 Header: Recalculating sections with aboutTitle:', aboutTitle);
    return [
      { id: 'about', label: aboutTitle },
      { id: 'posters', label: 'Aktuelles Konzert' },
      { id: 'news', label: 'Aktuelles' },
      ...customSections.map(section => ({
        id: `custom-section-${section.id}`,
        label: section.title
      })),
      { id: 'gallery', label: 'Galerie' },
      { id: 'vereine', label: 'Unsere Vereine' },
      ...(contactFormEnabled ? [{ id: 'contact-form', label: contactFormTitle }] : []),
      { id: 'contact', label: 'Kontakt' }
    ];
  }, [aboutTitle, customSections, contactFormEnabled, contactFormTitle]);

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      window.location.href = '/#' + id;
      return;
    }
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 shadow-md z-50 backdrop-blur-sm" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <nav className="container mx-auto px-4 py-4" aria-label="Hauptnavigation">
        <div className="flex justify-between items-center">
          <Link 
            to="/" 
            onClick={handleLogoClick}
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="Zur Startseite - StimmArt"
          >
            {headerLogoUrl ? (
              <img 
                src={headerLogoUrl} 
                alt="StimmArt Logo" 
                className="h-[40px] md:h-[60px] w-auto object-contain"
              />
            ) : (
              <h1 className="text-2xl font-bold text-[var(--primary-color)]">StimmArt</h1>
            )}
          </Link>
          
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <div className="hidden md:block"><ThemeToggle /></div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex space-x-8" role="navigation">
            {sections.map((section) => (
              <button 
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="hover:text-[var(--primary-color)] transition-colors"
                style={{ color: 'var(--text-primary)' }}
                aria-label={`Zum Abschnitt ${section.label} scrollen`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div 
            id="mobile-menu" 
            className="md:hidden mt-4 space-y-4"
            role="navigation"
          >
            {sections.map((section) => (
              <button 
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="block w-full text-left px-4 py-2 hover:bg-[var(--primary-color)]/10 transition-colors"
                style={{ color: 'var(--text-primary)' }}
                aria-label={`Zum Abschnitt ${section.label} scrollen`}
              >
                {section.label}
              </button>
            ))}
            <div className="pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <ThemeToggle />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;

